//
// Naltrexone/bupropion (Mysimba®) PGD — AHT weight management
// Source: AHT_Mysimba_Clinical_Pathway.docx v1.0
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Quantity basis — from source document:
//   Initial (weeks 1–4, escalation): up to 56 tablets (F2F required)
//   Subsequent supply: up to 112 tablets per episode (28 days at maintenance)
//   Max 3 subsequent episodes (max 336 tablets) before mandatory 16-week efficacy review
//
// Black Triangle medicine — ALL suspected ADRs must be reported to MHRA Yellow Card.
// Tablets must be swallowed whole — do not cut, chew, or crush.

import type { PGD } from "../../types";

export const pgdAhtWtMysimba: PGD = {
  id: "pgd-aht-wt-mysimba",
  reference: "AHT-WT-MYS",
  version: "1.0",
  drugRef: "mysimba",
  indication: "Weight management — naltrexone/bupropion (Mysimba®) 8mg/90mg prolonged-release tablets",
  validFrom: "",         // TODO: insert valid from date when signed
  expiryDate: "",        // TODO: insert expiry date when signed
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 18, max: 74 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
    notes: "Age 75+: not recommended per SmPC. BP and pulse must be measured and recorded BEFORE supply — controlled hypertension is an inclusion criterion.",
  },

  consultationRequirements: {
    initialSupply: "face_to_face",
    subsequentSupply: "remote_permitted",
  },

  duration: {
    days: 28,
    comment: "Escalation (weeks 1–4): up to 56 tablets. Maintenance: up to 112 tablets per episode (28 days × 4 tablets/day). Max 3 subsequent episodes before mandatory 16-week efficacy review.",
  },

  doseBands: [
    {
      id: "mys-week1",
      doseStepId: "mys-week1",
      resolveBy: "single",
      dose: "8mg/90mg × 1 morning",
      frequency: "Once daily (morning only)",
      comment: "Week 1: starting dose — take with food; swallow whole",
      formulations: [
        {
          formulationId: "mysimba-8mg-90mg-tablet",
          instruction: "Take 1 tablet in the morning with food. Swallow whole — do not cut, chew, or crush.",
          quantity: { count: 7, label: "7 tablets (week 1)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 7, max: 14 },
        },
      ],
    },
    {
      id: "mys-week2",
      doseStepId: "mys-week2",
      resolveBy: "single",
      dose: "8mg/90mg × 1 morning + 1 evening",
      frequency: "Twice daily",
      comment: "Week 2: +1 tablet if tolerated",
      formulations: [
        {
          formulationId: "mysimba-8mg-90mg-tablet",
          instruction: "Take 1 tablet in the morning and 1 tablet in the evening with food. Swallow whole — do not cut, chew, or crush.",
          quantity: { count: 14, label: "14 tablets (week 2)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 7, max: 14 },
        },
      ],
    },
    {
      id: "mys-week3",
      doseStepId: "mys-week3",
      resolveBy: "single",
      dose: "8mg/90mg × 2 morning + 1 evening",
      frequency: "Three tablets daily",
      comment: "Week 3: +1 tablet if tolerated",
      formulations: [
        {
          formulationId: "mysimba-8mg-90mg-tablet",
          instruction: "Take 2 tablets in the morning and 1 tablet in the evening with food. Swallow whole — do not cut, chew, or crush.",
          quantity: { count: 21, label: "21 tablets (week 3)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 14, max: 21 },
        },
      ],
    },
    {
      id: "mys-week4-maintenance",
      doseStepId: "mys-maintenance",
      resolveBy: "single",
      dose: "8mg/90mg × 2 morning + 2 evening",
      frequency: "Four tablets daily (MAXIMUM)",
      comment: "Week 4+: maintenance dose — MAX 4 tablets/day (32mg/360mg daily). Dose reduction required for moderate/severe renal impairment or mild hepatic impairment (Child-Pugh A): max 2 tablets/day.",
      formulations: [
        {
          formulationId: "mysimba-8mg-90mg-tablet",
          instruction: "Take 2 tablets in the morning and 2 tablets in the evening with food. Swallow whole — do not cut, chew, or crush.",
          quantity: { count: 112, label: "112 tablets (28-day maintenance supply)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 14, max: 112 },
        },
      ],
    },
  ],

  preSupplyChecks: [
    // BP MEASUREMENT — positive inclusion requirement (Source: Step 2)
    {
      id: "inc-bp-measured-recorded",
      question: "Has blood pressure and pulse been measured and recorded NOW before proceeding?",
      hint: "This is a positive inclusion requirement per the Mysimba PGD. Controlled hypertension is an inclusion criterion. Uncontrolled hypertension is an absolute contraindication — do not supply if BP uncontrolled.",
      direction: "exclude_on_no",
      linkedVitalType: "blood-pressure",
    },
    // EXCLUSION CRITERIA — Source: Step 3 of AHT Mysimba pathway v1.0
    {
      id: "ex-bmi-below-27",
      question: "Is the patient's BMI below 27 kg/m²?",
      autoAnswerFrom: "bmiBelowThreshold",
    },
    {
      id: "ex-bmi-27-30-no-comorbidity",
      question: "Is the patient's BMI 27–30 kg/m² without a weight-related comorbidity?",
      autoAnswerFrom: "bmiInLowRangeNoComorbidity",
    },
    {
      id: "ex-age-under-18",
      question: "Is the patient under 18 years of age?",
      patientGroup: "adult",
      autoAnswerFrom: "age_under_18",
    },
    {
      id: "ex-age-75-plus",
      question: "Is the patient aged 75 years or over?",
      hint: "Not recommended per SmPC.",
      patientGroup: "adult",
      autoAnswerFrom: "age_under_75",
    },
    {
      id: "ex-uncontrolled-hypertension",
      question: "Does the patient have uncontrolled hypertension?",
      hint: "Absolute contraindication per SmPC. Do not supply if BP uncontrolled.",
    },
    {
      id: "ex-seizure-disorder",
      question: "Does the patient have a current seizure disorder or history of seizures?",
      autoAnswerFrom: "q8SeizuresCns",
    },
    {
      id: "ex-cns-tumour",
      question: "Does the patient have a known central nervous system tumour?",
      autoAnswerFrom: "q8SeizuresCns",
    },
    {
      id: "ex-bulimia-anorexia",
      question: "Does the patient have a current or previous diagnosis of bulimia nervosa or anorexia nervosa?",
    },
    {
      id: "ex-bipolar",
      question: "Does the patient have a history of bipolar disorder?",
    },
    {
      id: "ex-alcohol-benzo-withdrawal",
      question: "Is the patient undergoing acute alcohol or benzodiazepine withdrawal?",
    },
    {
      id: "ex-chronic-opioids",
      question: "Is the patient currently dependent on chronic opioids or opiate agonists (e.g. methadone) or in acute opiate withdrawal?",
      autoAnswerFrom: "q10Opioids",
    },
    {
      id: "ex-opioid-use-7-10-days",
      question: "Has the patient used opioids within the last 7–10 days and cannot confirm being opioid-free for at least 7–10 days?",
      hint: "Do not supply if patient cannot confirm opioid-free for at least 7–10 days.",
      autoAnswerFrom: "q10Opioids",
    },
    {
      id: "ex-maoi-current-recent",
      question: "Is the patient currently taking or has recently stopped an MAOI (within 14 days)?",
      hint: "14-day washout required before starting Mysimba.",
      autoAnswerFrom: "q11Maois",
    },
    {
      id: "ex-concurrent-bupropion-naltrexone",
      question: "Is the patient taking bupropion or naltrexone in any other formulation?",
    },
    {
      id: "ex-hypersensitivity",
      question: "Does the patient have known hypersensitivity to naltrexone, bupropion, or any excipient (including lactose)?",
    },
    {
      id: "ex-severe-hepatic",
      question: "Does the patient have severe hepatic impairment (Child-Pugh C)?",
    },
    {
      id: "ex-moderate-hepatic",
      question: "Does the patient have moderate hepatic impairment (Child-Pugh B)?",
      hint: "Not recommended per SmPC.",
    },
    {
      id: "ex-end-stage-renal",
      question: "Does the patient have end-stage renal failure?",
    },
    {
      id: "ex-suicidal-ideation",
      question: "Does the patient have active suicidal ideation or a history of suicide attempt?",
    },
    {
      id: "ex-concurrent-weight-loss-agents",
      question: "Is the patient currently using other weight loss agents?",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-lactose-intolerance",
      question: "Does the patient have lactose intolerance, galactosaemia, or glucose-galactose malabsorption?",
    },
    {
      id: "ex-brugada-sudden-cardiac",
      question: "Does the patient have Brugada syndrome or a family history of sudden cardiac death or cardiac arrest?",
    },
    {
      id: "ex-virtual-consultation",
      question: "Is this a virtual or online consultation (for initial supply)?",
      hint: "Initial supply must be face-to-face at a registered GPhC/CQC premises.",
      autoAnswerFrom: "face_to_face_confirmed",
    },
  ],

  interactions: [
    {
      id: "int-maois",
      interactionName: "MAOIs",
      question: "Is the patient taking any MAOI?",
      hint: "Absolute contraindication — 14-day washout required before starting Mysimba.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: true,
      managementPlanRequired: false,
    },
    {
      id: "int-opioid-analgesics",
      interactionName: "Opioid analgesics",
      question: "Is the patient taking opioid analgesics?",
      hint: "Reduced analgesia due to opioid blockade — temporarily discontinue Mysimba for intermittent opioid use.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-cyp2d6-substrates",
      interactionName: "CYP2D6 substrates",
      question: "Is the patient taking CYP2D6 substrates (SSRIs, TCAs, antipsychotics, metoprolol, propafenone, flecainide)?",
      hint: "Bupropion inhibits CYP2D6 — initiate at lower dose; TDM for narrow-index drugs.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-cyp2b6-inducers",
      interactionName: "CYP2B6 inducers",
      question: "Is the patient taking CYP2B6 inducers (carbamazepine, phenytoin, ritonavir, efavirenz)?",
      hint: "May significantly reduce bupropion exposure.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-seizure-threshold-lowering",
      interactionName: "Drugs lowering seizure threshold",
      question: "Is the patient taking drugs that lower seizure threshold (antipsychotics, antidepressants, tramadol, theophylline, quinolones, steroids, antimalarials)?",
      hint: "Increased seizure risk with combination.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-levodopa-amantadine",
      interactionName: "Levodopa or amantadine",
      question: "Is the patient taking levodopa or amantadine?",
      hint: "Higher incidence of neuropsychiatric ADRs.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-digoxin",
      interactionName: "Digoxin",
      question: "Is the patient taking digoxin?",
      hint: "Co-administration may reduce plasma digoxin — monitor.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-valproate",
      interactionName: "Valproate",
      question: "Is the patient taking valproate?",
      hint: "May inhibit bupropion metabolism — monitor.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-tamoxifen",
      interactionName: "Tamoxifen",
      question: "Is the patient taking tamoxifen?",
      hint: "CYP2D6 inhibition may reduce tamoxifen efficacy — consider alternative.",
      affectedDrugs: ["mysimba"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
  ],

  cautions: [
    {
      id: "caut-bp-pulse-monitoring",
      text: "BP and pulse: measure before initiation, after each dose escalation, and at every review",
      adviceText: "Post-marketing hypertensive crisis reported during titration phase — reinforce monitoring at every escalation step.",
    },
    {
      id: "caut-seizure-risk",
      text: "Seizure risk (~0.1%) dose-related: predisposing factors include head trauma, excessive alcohol, cocaine/stimulants, concomitant drugs lowering seizure threshold",
    },
    {
      id: "caut-suicidality",
      text: "Suicidality: monitor closely for clinical worsening, suicidal ideation, unusual behavioural changes — especially first weeks and at dose changes; advise patient/carer to seek urgent advice if these emerge",
    },
    {
      id: "caut-opioid-blockade",
      text: "Opioid blockade: naltrexone blocks opioid receptors — patients will not receive full pain relief from opioids; warn explicitly and advise to carry patient card",
      adviceText: "Attempting to overcome opioid blockade with large opioid doses is dangerous and potentially fatal.",
    },
    {
      id: "caut-serotonin-syndrome",
      text: "Serotonin syndrome risk with SSRIs/SNRIs — monitor carefully if co-prescribing clinically necessary",
    },
    {
      id: "caut-hepatotoxicity",
      text: "Hepatotoxicity: drug-induced liver injury reported — discontinue if suspected",
    },
    {
      id: "caut-neuropsychiatric",
      text: "Neuropsychiatric symptoms: activation of mania/hypomania, panic attacks — monitor; use with caution in psychiatric history",
    },
    {
      id: "caut-cv-disease",
      text: "CV disease (recent MI, unstable disease, NYHA III/IV): no clinical experience — use with caution",
    },
    {
      id: "caut-renal-impairment",
      text: "Moderate/severe renal impairment: maximum 2 tablets/day",
    },
    {
      id: "caut-mild-hepatic",
      text: "Mild hepatic impairment (Child-Pugh A): maximum 2 tablets/day",
    },
    {
      id: "caut-elderly",
      text: "Elderly 65–74: use with caution; higher CNS sensitivity; monitor renal function",
    },
    {
      id: "caut-t2dm-glucose",
      text: "T2DM: may lower blood glucose — review antidiabetic doses to minimise hypoglycaemia",
    },
    {
      id: "caut-alcohol",
      text: "Alcohol: minimise or avoid throughout treatment",
    },
    {
      id: "caut-driving",
      text: "Driving: somnolence, dizziness, seizure risk — advise not to drive if affected",
    },
    {
      id: "caut-dry-mouth",
      text: "Dry mouth: advise good oral hygiene and dental review",
    },
  ],

  counselling: [
    "Supply in original packaging with PIL and patient card",
    "Swallow whole with water, with food — do not cut, chew, or crush",
    "Provide written dose escalation schedule",
    "Advise patient to carry patient card AT ALL TIMES (opioid blockade warning)",
    "BP and pulse check recorded before first supply",
    "Record batch number and expiry date",
    "Will not receive full pain relief from opioid analgesics — tell ALL clinicians, dentists, and paramedics",
    "Attempting to override opioid blockade with large opioid doses can be fatal",
    "Report thoughts of self-harm, suicide, or unusual mood changes immediately",
    "Do not drive or operate machinery if dizziness, drowsiness, or seizures occur",
    "Avoid alcohol throughout treatment",
    "Advise patient to inform GP on initiation and at each significant clinical change",
    "Dry mouth increases dental decay risk — good oral hygiene and dental review",
    "Common side effects: nausea, constipation, headache, dizziness, insomnia — usually improve within 4 weeks",
    "Store below 30°C in original packaging",
    "Documentation retained 8 years",
  ],

  efficacyReview: {
    timepoint: "16 weeks",
    threshold: "≥5% weight loss",
    actionIfNotMet: "Discontinue — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "BP and pulse: before initiation, after each dose escalation step, and at every review",
      "Weight",
      "BMI",
      "Waist circumference",
      "Mood, behaviour, and neuropsychiatric symptoms",
      "eGFR before initiation in patients with diabetes or elderly patients",
      "HbA1c if T2DM; review antidiabetic medication for hypoglycaemia risk after weight loss",
    ],
    discontinueIf: [
      "Less than 5% weight loss by week 16",
      "Seizure",
      "Clinically significant sustained BP/pulse rise",
      "Liver injury suspected",
      "Chronic opioids required",
      "Pregnancy",
      "Suicidal ideation",
    ],
  },

  reviewSchedule: {
    supplyIntervalDays: 28,
    drugSpecificReviewDays: 112,
    drugSpecificReviewLabel: "16-week efficacy review",
    efficacyThresholdPercent: 5,
    stopRuleDescription: "Discontinue if <5% weight loss by week 16 — stop treatment, refer to GP, offer lifestyle support",
    sectionBReviewDays: 168,
    bmiFloorKgM2: null,
    titrationSchedule: {
      weeklyUntilWeek: 4,
      weeklyIntervalDays: 7,
    },
    maxEpisodesBeforeReview: 3,
    requiresBpPulseAtEscalation: true,
    requiresBrandAndBatchAtEverySupply: false,
  },

  adverseEventGuidance: [
    "Seizure: STOP immediately — do not restart",
    "Suicidal ideation/self-harm: urgent referral to GP/mental health services",
    "Hypertensive crisis: STOP — refer urgently",
    "Liver injury suspected: discontinue",
    "Anaphylaxis/angioedema: STOP — refer urgently",
    "Report locally, notify GP, MHRA Yellow Card (yellowcard.mhra.gov.uk)",
    "Black Triangle medicine — ALL suspected ADRs must be reported",
  ],
};
